import { motion } from "framer-motion";
import { ExternalLink, Handshake, Shield, Wrench, Building2, Cpu } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import CTABanner from "@/components/CTABanner";

export default function Partners() {
  const partners = [
    {
      name: "Add Insure",
      logo: "/assets/img/partner-add-insure.png",
      website: "https://www.addinsure.com",
      category: "Insurance Partner",
      icon: Shield,
      description: "Add Insure has been an invaluable partner in ensuring Aussie Inspections maintains comprehensive insurance coverage. As a trusted insurance brokerage, they've gone above and beyond to understand the unique risks and requirements of the property inspection industry.",
      highlights: [
        "Tailored professional indemnity insurance solutions",
        "Responsive and proactive service approach",
        "Expert guidance on industry-specific coverage",
        "Competitive rates with comprehensive protection"
      ],
      testimonial: "Add Insure has supported us through every stage of our growth, ensuring we have the right coverage to protect our clients and our business. Their expertise in the property services sector has been instrumental in giving us peace of mind."
    },
    {
      name: "Australian Maintenance Company",
      logo: "/assets/img/partner-ausmco.png",
      website: "https://www.ausmco.com.au",
      category: "Remediation Partner",
      icon: Wrench,
      description: "Australian Maintenance Company (AusMCo) is our trusted remediation partner, helping our clients address issues identified during inspections. Their professional approach and quality workmanship have made them an essential part of our service ecosystem.",
      highlights: [
        "Expert remediation and maintenance services",
        "Transparent quoting and project management",
        "Licensed and fully insured contractors",
        "Darwin and NT specialists"
      ],
      testimonial: "Working with AusMCo has been a pleasure. When our inspections identify issues that need attention, we confidently refer our clients to AusMCo knowing they'll receive the same level of professionalism and care that we provide. Their team consistently delivers quality results on time and on budget."
    },
    {
      name: "Real Estate Central",
      logo: "/assets/img/partner-real-estate-central.png",
      website: "https://www.recentral.com.au",
      category: "Real Estate Partner",
      icon: Building2,
      description: "Real Estate Central is one of Darwin's leading real estate agencies, and we're proud to work alongside them to ensure smooth property transactions for their clients. Our partnership has helped countless buyers and sellers navigate the inspection process with confidence.",
      highlights: [
        "Seamless coordination on property inspections",
        "Priority scheduling for their clients",
        "Clear communication throughout the process",
        "Proven track record of successful settlements"
      ],
      testimonial: "Our collaboration with Real Estate Central has been built on a shared commitment to client satisfaction. By working together, we've helped numerous clients avoid potential issues at settlement time, ensuring property transactions proceed smoothly and without unexpected surprises."
    },
    {
      name: "NT Development Group",
      logo: "/assets/img/partner-nt-development-group.png",
      website: "https://www.ntdg.com.au",
      category: "Technology & Operations Partner",
      icon: Cpu,
      description: "NT Development Group has been instrumental in helping Aussie Inspections modernise our operations and scale our business. From technology solutions to back-office staffing, NTDG has provided the support we need to focus on what we do best – delivering quality inspections.",
      highlights: [
        "Custom technology solutions for inspection workflows",
        "Professional back-office staffing support",
        "Digital transformation consulting",
        "Scalable systems for business growth"
      ],
      testimonial: "NTDG has transformed how we operate. Their technology solutions have streamlined our inspection scheduling, reporting, and client communication. The back-office support they provide allows our inspectors to focus on delivering thorough, professional inspections rather than administrative tasks."
    }
  ];

  const partnerSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Aussie Inspections",
    "url": "https://aussieinspections.com.au",
    "partner": partners.map(partner => ({
      "@type": "Organization",
      "name": partner.name,
      "url": partner.website
    }))
  };

  return (
    <div>
      <Helmet>
        <title>Our Partners | Trusted Business Partners | Aussie Inspections Darwin</title>
        <meta name="description" content="Meet our trusted partners who help us deliver exceptional property inspection services in Darwin. Insurance, remediation, real estate and technology partners working together." />
        <link rel="canonical" href="https://aussieinspections.com.au/partners" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Partners | Aussie Inspections Darwin" />
        <meta property="og:description" content="Meet our trusted partners who help us deliver exceptional property inspection services in Darwin." />
        <meta property="og:url" content="https://aussieinspections.com.au/partners" />
        <script type="application/ld+json">
          {JSON.stringify(partnerSchema)}
        </script>
      </Helmet>

      {/* CTA Banner */}
      <CTABanner />

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <Handshake className="w-16 h-16 text-secondary" />
            </div>
            <h1 className="font-chivo text-5xl md:text-6xl font-black tracking-tight leading-none mb-4">
              Our Trusted Partners
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              At Aussie Inspections, we believe in the power of collaboration. We&apos;re proud to work with 
              these outstanding organisations who share our commitment to quality and client satisfaction.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partners Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {partners.map((partner, index) => {
              const IconComponent = partner.icon;
              return (
                <motion.div
                  key={partner.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`flex flex-col ${index % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-12 items-center`}
                >
                  {/* Logo Section */}
                  <div className="w-full lg:w-1/3">
                    <div className="rounded-2xl p-8 flex items-center justify-center min-h-[200px]">
                      {partner.logo ? (
                        <img
                          src={partner.logo}
                          alt={`${partner.name} logo`}
                          className="max-w-full max-h-32 object-contain"
                        />
                      ) : (
                        <div className="text-center">
                          <IconComponent className="w-16 h-16 text-primary mx-auto mb-3" />
                          <span className="font-chivo text-2xl font-bold text-slate-800">{partner.name}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="w-full lg:w-2/3">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-primary" />
                      </div>
                      <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                        {partner.category}
                      </span>
                    </div>

                    <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4">
                      {partner.name}
                    </h2>

                    <p className="text-slate-600 mb-6 text-lg leading-relaxed">
                      {partner.description}
                    </p>

                    {/* Highlights */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                      {partner.highlights.map((highlight, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <div className="w-5 h-5 bg-secondary/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <div className="w-2 h-2 bg-secondary rounded-full" />
                          </div>
                          <span className="text-slate-700 text-sm">{highlight}</span>
                        </div>
                      ))}
                    </div>

                    {/* Testimonial Quote */}
                    <blockquote className="border-l-4 border-secondary pl-4 py-2 bg-slate-50 rounded-r-lg mb-6">
                      <p className="text-slate-600 italic text-sm leading-relaxed">
                        &ldquo;{partner.testimonial}&rdquo;
                      </p>
                    </blockquote>

                    {/* CTA Button */}
                    <Button
                      asChild
                      variant="outline"
                      className="border-primary text-primary hover:bg-primary hover:text-white"
                    >
                      <a
                        href={partner.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2"
                      >
                        Visit {partner.name}
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Become a Partner CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4">
              Interested in Partnering With Us?
            </h2>
            <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
              We&apos;re always looking to connect with businesses that share our values of quality, 
              integrity, and client satisfaction. If you&apos;re interested in exploring partnership 
              opportunities, we&apos;d love to hear from you.
            </p>
            <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90">
              <a href="/contact">Get in Touch</a>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
